/**
 * A booking reads as a table: its labels in the first column, then the partner's statement and
 * the host's answer side by side — or the statement alone, where there is no answer to stand
 * beside it. The settlement beneath the bookings stands on the same template, so each total sits
 * under the column it adds up.
 *
 * @param hasHostColumn - Whether the host's answer takes a column of its own.
 * @returns The grid's classes, both columns' or the statement's alone.
 */
export function getBookingGridClassName(hasHostColumn: boolean) {
	return hasHostColumn
		? "grid grid-cols-[6rem_minmax(0,1fr)_minmax(0,1fr)] gap-x-6"
		: "grid grid-cols-[6rem_minmax(0,1fr)] gap-x-6";
}

/**
 * The partner's column keeps full strength where its inputs are disabled — in the host's view and
 * on an approved record — because there they are read, and a dimmed figure reads as one that does
 * not count. Inputs dim through `:disabled` and the textarea through `input-disabled`, so both are
 * named, and the override is important because the `:disabled` rule sits outside every layer.
 */
export const submittedColumnClassName =
	"[&_input:disabled]:opacity-100! [&.input-disabled]:opacity-100!";

/** The two sides of a booking, in the order their columns stand. */
export const bookingInputRoles = ["partner", "host"] as const;

export type BookingInputRole = (typeof bookingInputRoles)[number];

/**
 * Where a side's values live in the form: the partner's on the booking itself, the host's in the
 * booking's adjustment.
 *
 * @param inputRole - The side whose field is addressed.
 * @returns The path segment between `bookings.<index>.` and the field name.
 */
export function getBookingFieldPrefix(inputRole: BookingInputRole): "" | "adjustment." {
	return inputRole === "partner" ? "" : "adjustment.";
}
